import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ShareButtons from '../components/ShareButtons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Clock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const ListingDetailPage = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: '',
    cover_letter: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchListing = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/listings/${id}`);
      setListing(data);
    } catch (error) {
      console.error('Error fetching listing:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, id]);

  useEffect(() => {
    fetchListing();
  }, [fetchListing]);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const daysUntil = (isoDate) => {
    if (!isoDate) return null;
    const ms = new Date(isoDate).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/applications`, {
        listing_id: id,
        ...formData
      });
      toast.success('Application submitted successfully!');
      setShowApplicationForm(false);
      setFormData({ applicant_name: '', applicant_email: '', cover_letter: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const isHtmlContent = (text) => {
    return text && (text.includes('<p>') || text.includes('<h') || text.includes('<ul>') || text.includes('<ol>') || text.includes('<strong>'));
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
        <Navbar />
        <div className="px-6 md:px-12 lg:px-24 py-20">
          <p style={{ color: '#0F2942' }}>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
        <Navbar />
        <div className="px-6 md:px-12 lg:px-24 py-20">
          <p className="text-xl" style={{ color: '#0F2942' }}>Listing not found</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0EBE2' }}>
      <Navbar />

      <article className="px-6 md:px-12 lg:px-24 py-20">
        <div className="max-w-4xl">
          <Link to="/browse" className="text-sm hover:underline mb-8 inline-block" style={{ color: '#0F2942' }} data-testid="back-to-listings">
            &larr; Back to listings
          </Link>

          {(() => {
            // Employment type mapping for schema.org JobPosting
            const employmentTypeMap = {
              'Full-Time Jobs': 'FULL_TIME',
              'Internships': 'INTERN',
              'Freelance / Gigs': 'CONTRACTOR',
              'Contract Roles': 'CONTRACTOR',
              'Volunteer Opportunities': 'VOLUNTEER',
              'Teaching & Education': 'FULL_TIME',
              'Events & Festivals': 'TEMPORARY',
              'Speaking Opportunities': 'TEMPORARY'
            };
            const employmentType = employmentTypeMap[listing.opportunity_type] || 'OTHER';
            const plainDesc = (listing.description || '').replace(/<[^>]*>/g, '').trim();
            const shortDesc = plainDesc.length > 140
              ? plainDesc.substring(0, 150).replace(/\s+\S*$/, '') + '…'
              : plainDesc;

            return (
              <SEO
                title={`${listing.title} at ${listing.organisation}, ${listing.city}`}
                path={`/listing/${listing.id}`}
                type="article"
                orgName={listing.organisation}
                description={shortDesc || `${listing.opportunity_type} at ${listing.organisation} in ${listing.city}, ${listing.country}.`}
                jsonLd={{
                  "@context": "https://schema.org",
                  "@type": "JobPosting",
                  "title": listing.title,
                  "description": listing.description,
                  "identifier": {
                    "@type": "PropertyValue",
                    "name": "CultureCareers",
                    "value": listing.id
                  },
                  "datePosted": listing.created_at,
                  "validThrough": listing.closing_date,
                  "employmentType": employmentType,
                  "hiringOrganization": {
                    "@type": "Organization",
                    "name": listing.organisation
                  },
                  "jobLocation": {
                    "@type": "Place",
                    "address": {
                      "@type": "PostalAddress",
                      "addressLocality": listing.city,
                      "addressRegion": listing.city,
                      "addressCountry": listing.country || "India"
                    }
                  },
                  "jobLocationType": /remote/i.test(listing.city || '') ? "TELECOMMUTE" : undefined,
                  "industry": listing.industry,
                  "baseSalary": listing.salary ? {
                    "@type": "MonetaryAmount",
                    "currency": "INR",
                    "value": {
                      "@type": "QuantitativeValue",
                      "value": listing.salary,
                      "unitText": "YEAR"
                    }
                  } : undefined,
                  "url": `https://culturecareers.org/listing/${listing.id}`
                }}
              />
            );
          })()}

          <div className="space-y-8">
            {/* Tags — max 5, curated for scannability */}
            {(() => {
              const days = daysUntil(listing.closing_date);
              const primary = listing.opportunity_type;
              // Pick top 2 skill tags (if any) after industry + city — keeping total ≤ 5
              const supporting = [];
              if (listing.industry) supporting.push({ label: listing.industry, tone: 'industry' });
              if (listing.city) supporting.push({ label: listing.city, tone: 'meta' });
              (listing.tags || []).slice(0, 2).forEach((t) =>
                supporting.push({ label: t, tone: 'skill' })
              );
              const limited = supporting.slice(0, 3); // total = primary + 3 + deadline = max 5
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1" style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}>
                    {primary}
                  </span>
                  {limited.map((t) => {
                    const bg =
                      t.tone === 'industry'
                        ? 'rgba(212,130,10,0.15)'
                        : t.tone === 'meta'
                        ? '#F0EBE2'
                        : '#F0EBE2';
                    const color =
                      t.tone === 'industry' ? '#D4820A' : '#0F2942';
                    return (
                      <span
                        key={t.label}
                        className="text-xs tracking-wide rounded-full px-2.5 py-0.5 uppercase tracking-[0.15em] font-medium"
                        style={{ backgroundColor: bg, color }}
                      >
                        {t.label}
                      </span>
                    );
                  })}
                  {days !== null && days <= 7 && days >= 0 && (
                    <span
                      className="text-xs uppercase tracking-[0.15em] font-semibold rounded-full px-3 py-1"
                      style={{ backgroundColor: 'rgba(212,130,10,0.12)', color: '#D4820A' }}
                      data-testid="deadline-soon-pill"
                    >
                      Deadline Soon
                    </span>
                  )}
                </div>
              );
            })()}

            {/* Title & Org */}
            <div>
              <h1
                className="text-4xl md:text-5xl font-normal tracking-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}
                data-testid="listing-title"
              >
                {listing.title}
              </h1>
              <p className="text-xl font-medium" style={{ color: '#0F2942' }}>{listing.organisation}</p>
            </div>

            {/* Meta */}
            <div className="flex flex-wrap gap-6 py-6" style={{ borderTop: '1px solid #F0EBE2', borderBottom: '1px solid #F0EBE2' }}>
              <div className="flex items-center gap-2 text-base" style={{ color: '#0F2942' }}>
                <MapPin className="w-5 h-5" strokeWidth={1.5} />
                <span>{listing.city}, {listing.country}</span>
              </div>
              <div className="flex items-center gap-2 text-base" style={{ color: '#0F2942' }}>
                <Clock className="w-5 h-5" strokeWidth={1.5} />
                <span>Closes {formatDate(listing.closing_date)}</span>
              </div>
            </div>

            {listing.salary && (
              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] font-medium mb-2" style={{ color: '#0F2942' }}>Compensation</h3>
                <p className="text-lg font-medium" style={{ color: '#0F2942' }}>{listing.salary}</p>
              </div>
            )}

            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] font-medium mb-4" style={{ color: '#0F2942' }}>About the Opportunity</h3>
              {isHtmlContent(listing.description) ? (
                <div
                  className="prose prose-lg max-w-none prose-headings:font-medium prose-headings:tracking-tight prose-p:font-light prose-p:leading-relaxed prose-a:underline"
                  style={{ fontFamily: 'Work Sans, sans-serif', color: '#0F2942' }}
                  dangerouslySetInnerHTML={{ __html: listing.description }}
                  data-testid="listing-description"
                />
              ) : (
                <p className="text-base leading-relaxed font-light whitespace-pre-wrap" style={{ color: '#0F2942' }} data-testid="listing-description">
                  {listing.description}
                </p>
              )}
            </div>

            {/* Apply Section */}
            <div className="pt-8 space-y-4" style={{ borderTop: '1px solid #F0EBE2' }}>
              <h3 className="text-xl font-medium" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0F2942' }}>
                How to Apply
              </h3>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {listing.apply_link && (
                  <a href={listing.apply_link} target="_blank" rel="noopener noreferrer">
                    <Button className="rounded-lg px-6 py-3 w-full sm:w-auto" style={{ backgroundColor: '#D4820A', color: '#0F2942' }} data-testid="external-apply-button">
                      Apply on Company Website <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                )}
                
                {listing.allow_platform_application && (
                  <Button
                    onClick={() => setShowApplicationForm(!showApplicationForm)}
                    variant="outline"
                    className="rounded-lg px-6 py-3 w-full sm:w-auto"
                    style={{ borderColor: '#0F2942', color: '#0F2942' }}
                    data-testid="platform-apply-button"
                  >
                    {showApplicationForm ? 'Hide Form' : 'Apply Here'}
                  </Button>
                )}
              </div>

              {/* Share buttons */}
              <ShareButtons
                title={listing.title}
                organisation={listing.organisation}
                url={typeof window !== 'undefined' ? window.location.href : `https://culturecareers.org/listing/${listing.id}`}
              />

              {showApplicationForm && (
                <form onSubmit={handleSubmitApplication} className="mt-8 space-y-6 p-8 rounded-xl bg-white shadow-sm" data-testid="application-form">
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Your Name</label>
                    <Input
                      type="text"
                      value={formData.applicant_name}
                      onChange={(e) => setFormData({ ...formData, applicant_name: e.target.value })}
                      required
                      className="rounded-lg"
                      data-testid="applicant-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Your Email</label>
                    <Input
                      type="email"
                      value={formData.applicant_email}
                      onChange={(e) => setFormData({ ...formData, applicant_email: e.target.value })}
                      required
                      className="rounded-lg"
                      data-testid="applicant-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: '#0F2942' }}>Cover Letter</label>
                    <Textarea
                      value={formData.cover_letter}
                      onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
                      required
                      rows={6}
                      className="rounded-lg"
                      data-testid="cover-letter-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-lg px-6 py-3"
                    style={{ backgroundColor: '#0F2942', color: '#FFFFFF' }}
                    data-testid="submit-application-button"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ListingDetailPage;
